import { useEffect, useRef, useState, useCallback } from 'react';
import { logger } from '@/utils/logger';

export type WebSocketEventType = 
  | 'match_started' 
  | 'match_updated' 
  | 'match_completed' 
  | 'score_updated' 
  | 'bracket_generated'
  | 'timer_control'
  | 'connected';

export interface WebSocketMessage {
  type: WebSocketEventType;
  tournamentId: number;
  data?: any;
  timestamp?: string;
  message?: string;
}

interface UseWebSocketOptions {
  tournamentId: number;
  userId?: number;
  role?: string;
  onMessage?: (message: WebSocketMessage) => void;
  onError?: (error: Event) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  autoReconnect?: boolean;
  reconnectInterval?: number;
  enabled?: boolean; // Опция для условного отключения WebSocket
}

interface UseWebSocketReturn {
  isConnected: boolean;
  sendMessage: (message: any) => void;
  reconnect: () => void;
  disconnect: () => void;
}

const getWebSocketUrl = (tournamentId: number, userId?: number, role?: string): string => {
  // Используем тот же механизм определения URL, что и для REST API
  if (typeof window === 'undefined') {
    // Fallback для SSR
    return `ws://localhost:3001/ws?tournamentId=${tournamentId}`;
  }

  const hostname = window.location.hostname;
  
  // Явная проверка на localhost варианты и локальные IP-адреса
  const isLocalhost = hostname === 'localhost' ||
                     hostname === '127.0.0.1' ||
                     hostname === '::1' ||
                     hostname === '0.0.0.0' ||
                     hostname.startsWith('192.168.') ||
                     hostname.startsWith('10.') ||
                     hostname.startsWith('172.');

  let wsUrl: string;
  
  if (isLocalhost) {
    // Для localhost используем прямой WebSocket на порт 3001
    wsUrl = 'ws://localhost:3001/ws';
    logger.debug('[WebSocket] Runtime detection: localhost detected, using:', wsUrl, 'hostname:', hostname);
  } else {
    // Для production используем относительный путь - nginx проксирует /ws на backend WebSocket
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    wsUrl = `${wsProtocol}//${window.location.host}/ws`;
    logger.debug('[WebSocket] Runtime detection: production server detected, using:', wsUrl, 'hostname:', hostname);
  }
  
  const params = new URLSearchParams({
    tournamentId: tournamentId.toString(),
  });
  
  if (userId) {
    params.append('userId', userId.toString());
  }
  
  if (role) {
    params.append('role', role);
  }
  
  const fullUrl = `${wsUrl}?${params.toString()}`;
  logger.debug('[WebSocket] Full WebSocket URL:', fullUrl);
  return fullUrl;
};

export const useWebSocket = ({
  tournamentId,
  userId,
  role,
  onMessage,
  onError,
  onConnect,
  onDisconnect,
  autoReconnect = true,
  reconnectInterval = 3000,
  enabled = true, // По умолчанию включен
}: UseWebSocketOptions): UseWebSocketReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;

  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  onMessageRef.current = onMessage;
  onErrorRef.current = onError;
  onConnectRef.current = onConnect;
  onDisconnectRef.current = onDisconnect;

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      logger.debug('WebSocket already connected');
      return;
    }

    try {
      const url = getWebSocketUrl(tournamentId, userId, role);
      logger.debug(`Connecting to WebSocket: ${url}`);

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        logger.info('WebSocket connected');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        onConnectRef.current?.();
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          logger.debug('WebSocket message received:', message);
          onMessageRef.current?.(message);
        } catch (error) {
          logger.error('Failed to parse WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        // WebSocket Event объекты не содержат полезной информации, детали в onclose
        // Логируем как DEBUG, так как многие ошибки WebSocket являются нормальными
        // (например, при недоступности сервера во время переподключения)
        // Также игнорируем ошибки Vite HMR WebSocket
        const errorEvent = error as any;
        const isViteHMR = errorEvent?.target?.url?.includes('vite') || 
                         errorEvent?.target?.url?.includes('localhost:3000') ||
                         errorEvent?.isTrusted === true && !tournamentId;
        
        if (isViteHMR) {
          // Игнорируем ошибки Vite HMR WebSocket - они не критичны
          return;
        }
        
        const url = getWebSocketUrl(tournamentId, userId, role);
        const readyState = ws.readyState;
        const stateNames = ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'];
        
        logger.debug(`WebSocket error event (expected during connection issues):`, {
          url,
          readyState: readyState !== undefined ? `${readyState} (${stateNames[readyState]})` : 'unknown',
          attempt: reconnectAttemptsRef.current + 1,
          maxAttempts: maxReconnectAttempts,
          isTrusted: errorEvent?.isTrusted
        });
        
        setIsConnected(false);
        onErrorRef.current?.(error);
      };

      ws.onclose = (event) => {
        const closeCode = event.code;
        const closeReason = event.reason || 'No reason provided';
        const wasClean = event.wasClean;
        
        // Логируем уровень в зависимости от кода закрытия и контекста
        const logLevel = (closeCode === 1006 || closeCode === 1001) && autoReconnect 
          ? 'debug' // Нормальное закрытие при переподключении
          : wasClean 
            ? 'info' 
            : 'warn';
        
        if (logLevel === 'debug') {
          logger.debug(`WebSocket disconnected (normal during reconnection):`, {
            code: closeCode,
            reason: closeReason,
            wasClean,
            attempt: reconnectAttemptsRef.current
          });
        } else if (logLevel === 'info') {
          logger.info(`WebSocket disconnected:`, {
            code: closeCode,
            reason: closeReason,
            wasClean
          });
        } else {
          logger.warn(`WebSocket disconnected unexpectedly:`, {
            code: closeCode,
            reason: closeReason,
            wasClean
          });
        }
        
        setIsConnected(false);
        onDisconnect?.();

        // Автоматическое переподключение
        if (autoReconnect && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          const delay = reconnectInterval * reconnectAttemptsRef.current;
          logger.debug(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          logger.error('Max reconnection attempts reached for WebSocket', {
            tournamentId,
            url: getWebSocketUrl(tournamentId, userId, role),
            totalAttempts: reconnectAttemptsRef.current
          });
        }
      };
    } catch (error) {
      logger.error('Failed to create WebSocket connection:', error);
      setIsConnected(false);
    }
  }, [tournamentId, userId, role, autoReconnect, reconnectInterval]);

  const sendMessage = useCallback((message: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      logger.warn('WebSocket is not connected, cannot send message');
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setIsConnected(false);
  }, []);

  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttemptsRef.current = 0;
    setTimeout(() => {
      connect();
    }, 1000);
  }, [connect, disconnect]);

  useEffect(() => {
    if (tournamentId && enabled) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [tournamentId, enabled]); // Подключаемся только при изменении tournamentId или enabled

  return {
    isConnected,
    sendMessage,
    reconnect,
    disconnect,
  };
};

